import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Icon, Badge, Skeleton } from '@/design-system';
import type { Bottle, VinmonopoletProduct } from '@/lib/api';
import { formatAbv, formatVolumeMl } from '@/lib/format';
import { motion } from 'framer-motion';

/**
 * Any mirrored catalog JPG (`/media/catalog-images/<source>__<id>.jpg`) has
 * a matching `?cutout=1` variant — a transparent-background WebP generated
 * and cached server-side (see backend/src/services/image-cutout.ts) that
 * strips the flat white studio background the Vinmonopolet/Systembolaget
 * catalog photos are shot on, leaving just the bottle. Mirrored PNGs
 * already carry real alpha (either a genuine pre-cut photo or a "no photo"
 * placeholder silhouette) so they're left untouched, and any other image
 * (live third-party CDN URLs used while browsing before a product is
 * imported/mirrored) is also left untouched — the regex simply won't match.
 */
const CUTOUT_ELIGIBLE = /\/media\/catalog-images\/[^/?#]+\.(jpe?g)(?=$|[?#])/i;

function cutoutSrc(src: string): string | null {
  if (!CUTOUT_ELIGIBLE.test(src)) return null;
  return `${src}${src.includes('?') ? '&' : '?'}cutout=1`;
}

/**
 * Mirrored catalog PNGs are never run through the cutout pipeline (see
 * cutoutSrc above) because they already carry real alpha transparency —
 * Systembolaget exports every product as a pre-cut PNG, and Vinmonopolet's
 * PNG rows are either a genuine pre-cut photo or a "no photo" placeholder
 * silhouette. Either way there is no background to remove.
 */
const NATIVE_TRANSPARENT = /\/media\/catalog-images\/[^/?#]+\.png(?=$|[?#])/i;

/**
 * Image with graceful fallback to a bottle glyph.
 *
 * Load order for any `src` that qualifies for a cutout (see `cutoutSrc`
 * above): the transparent `?cutout=1` variant first, falling back to the
 * original (white-background) photo if that 404s/errors, and finally to
 * the bottle glyph if the original fails too. Ineligible sources (external
 * CDN URLs, already-transparent PNGs) start straight at the original.
 *
 * Three rendering modes control the surrounding decoration, not whether
 * the background is removed — that happens for every mode via the cutout
 * variant:
 *
 *   - `mode="bare"` (default): just `object-contain` centred, plus a soft
 *     floor shadow so the cut-out bottle reads as floating on the card's
 *     own surface rather than pasted flat.
 *
 *   - `mode="lightbox"` (recommended for any slot ≥ ~80px): adds a soft
 *     cream "product photography" backdrop behind the bottle and a bigger
 *     floor shadow. `mix-blend-mode: multiply` is kept on the `<img>` so
 *     that ineligible (still white-background) sources still blend in
 *     instead of showing a harsh rectangle — a no-op on a transparent
 *     cutout image.
 *
 *   - `mode="backdrop-blur"`: legacy mode using a scaled, blurred copy of
 *     the same image as backdrop. Kept for places where the cream lightbox
 *     would clash with the surrounding design.
 */
export type BottleImageMode = 'bare' | 'lightbox' | 'backdrop-blur';

export function BottleImage({
  src,
  alt,
  iconSize = 32,
  className,
  mode = 'bare',
  /** Legacy alias for `mode="backdrop-blur"`. */
  withBackdrop,
  /**
   * Fires once there is no image left to try (no `src`, or every fallback
   * — cutout, then original — has 404'd/errored) and we're about to render
   * the plain bottle glyph. Callers that want a richer empty state (e.g.
   * `CatalogProductCard`'s category-labelled fallback) can use this to
   * render their own UI instead of the default glyph.
   */
  onFail,
}: {
  src?: string | null;
  alt?: string;
  iconSize?: number;
  className?: string;
  mode?: BottleImageMode;
  withBackdrop?: boolean;
  onFail?: () => void;
}) {
  // 'cutout' -> transparent variant, 'original' -> raw photo, 'failed' -> glyph.
  const [stage, setStage] = useState<'cutout' | 'original' | 'failed'>('cutout');
  useEffect(() => {
    setStage('cutout');
  }, [src]);

  const hasFailed = !src || stage === 'failed';
  useEffect(() => {
    if (hasFailed) onFail?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onFail intentionally not tracked: callers pass an inline closure, and re-invoking it on every render (rather than only on the true->false transition) is harmless since it's always a `setState(true)`.
  }, [hasFailed]);

  if (!src) {
    return <Icon name="bottle" size={iconSize} className="text-mist/40" />;
  }
  if (stage === 'failed') {
    return <Icon name="bottle" size={iconSize} className="text-mist/40" />;
  }

  const cutout = cutoutSrc(src);
  const resolvedSrc = stage === 'cutout' && cutout ? cutout : src;

  // True once we know for certain the pixels we're about to paint have no
  // background left to hide — either we're rendering the generated cutout
  // variant, or the source is a mirrored PNG that was already transparent
  // to begin with. False for anything still carrying its white studio
  // background (not-yet-mirrored external CDN URLs, or a cutout that 404'd
  // and fell back to the raw original) — those still need the cream
  // lightbox + multiply safety net below.
  const isKnownTransparent = (stage === 'cutout' && Boolean(cutout)) || NATIVE_TRANSPARENT.test(src);

  const handleError = () => {
    // First failure: if we were on the cutout variant, retry the plain
    // original photo (route falls back to it server-side already, but a
    // client-side retry also covers stale/partial cache states). Any other
    // failure means the image genuinely doesn't exist — show the glyph.
    setStage((current) => (current === 'cutout' && cutout ? 'original' : 'failed'));
  };

  const effectiveMode: BottleImageMode = withBackdrop ? 'backdrop-blur' : mode;

  if (effectiveMode === 'lightbox') {
    return (
      <>
        {/* Cream "lightbox" backdrop — skipped once we know the image has
            no background left to hide (see isKnownTransparent above), so an
            already-cut-out bottle floats directly on the surrounding
            card/surface instead of sitting in an unwanted cream box. Kept
            for sources that still carry their white studio background:
            `mix-blend-mode: multiply` on the image below blends that white
            into this cream instead of showing a harsh rectangle. */}
        {!isKnownTransparent && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 50% 30%, #f5efe2 0%, #e8dfc8 65%, #d6c9aa 100%)',
            }}
          />
        )}
        {/* Absolutely fill the parent so `object-contain` fits against the
            actual card height, not the image's intrinsic size. Otherwise,
            inside `grid place-items-center`, the auto-sized row matches the
            image (≈300px) and `max-h-full` no-ops, clipping the bottle. */}
        <img
          src={resolvedSrc}
          alt={alt ?? ''}
          loading="lazy"
          decoding="async"
          onError={handleError}
          style={isKnownTransparent ? undefined : { mixBlendMode: 'multiply' }}
          className={`absolute inset-0 h-full w-full object-contain object-center ${className ?? ''}`}
        />
        {/* Subtle floor shadow to ground the bottle */}
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-1/2 h-3 w-3/5 -translate-x-1/2 rounded-[50%] blur-md"
          style={{ background: 'rgba(0, 0, 0, 0.35)' }}
        />
      </>
    );
  }

  if (effectiveMode === 'backdrop-blur') {
    return (
      <>
        <img
          src={src}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover opacity-25 blur-2xl saturate-150"
        />
        <img
          src={resolvedSrc}
          alt={alt ?? ''}
          loading="lazy"
          decoding="async"
          onError={handleError}
          style={isKnownTransparent ? undefined : { mixBlendMode: 'multiply' }}
          className={`absolute inset-0 h-full w-full object-contain object-center ${className ?? ''}`}
        />
      </>
    );
  }

  // bare — absolutely fill the parent (which must be `position: relative` with
  // a definite size) so `object-contain` scales against the *container's*
  // actual box, not the image's intrinsic size. This mirrors the fix already
  // applied to `lightbox`/`backdrop-blur` above: inside a `grid
  // place-items-center` thumbnail (the common pattern for small product
  // photos across the app), a plain `h-full`/`max-h-full` on the <img> hits a
  // circular percentage-height dependency and silently resolves to the
  // image's intrinsic size instead of the container's — for the CDN's tall
  // portrait bottle crops that then gets clamped down to the container's
  // *width* by `max-w-full`, scaled proportionally, and ends up TALLER than
  // the container, so `overflow-hidden` clips it to just the top of the
  // bottle. Absolute positioning sidesteps the circularity entirely.
  //
  // `bare` is used for small in-list/in-table swatches (cellar table rows,
  // scan results, stats, pairing lists, …) whose `src` is very often a raw
  // third-party CDN photo (e.g. Vinmonopolet's own image host) rather than
  // our mirrored `/media/catalog-images/…` asset — those never qualify for
  // the `?cutout=1` transparent variant (see `cutoutSrc` above), so
  // `isKnownTransparent` is false and the JPEG's flat white studio
  // background is still baked into the pixels. Without a blend mode that
  // paints as a solid white rectangle sitting on top of the parent's
  // `bg-ink-soft` swatch — reading as "the list has a white background" even
  // though only the wrapper's design-system surface class is dark. `bare`
  // intentionally skips the `lightbox` cream backdrop (there's no room for
  // it at icon sizes), but still needs the same `mix-blend-mode: multiply`
  // safety net so an ineligible source's white pixels dissolve into
  // whatever surface they're sitting on instead of showing as a harsh box —
  // a no-op once the image genuinely is transparent.
  return (
    <>
      <img
        src={resolvedSrc}
        alt={alt ?? ''}
        loading="lazy"
        decoding="async"
        onError={handleError}
        style={isKnownTransparent ? undefined : { mixBlendMode: 'multiply' }}
        className={`absolute inset-0 h-full w-full object-contain object-center ${className ?? ''}`}
      />
      {/* Small floor shadow so a cut-out bottle grounds on the surface
          instead of looking like it's pasted flat. Harmless (and mostly
          hidden behind the photo) for ineligible sources that still carry
          their white background. */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/2 h-1.5 w-2/5 -translate-x-1/2 rounded-[50%] blur-sm"
        style={{ background: 'rgba(0, 0, 0, 0.28)' }}
      />
    </>
  );
}

/* Adapter so the same card renders both real Bottles and raw catalog products. */
type Source =
  | { kind: 'bottle'; bottle: Bottle }
  | { kind: 'catalog'; product: VinmonopoletProduct };

interface BottleCardProps {
  /** Pass either `bottle` (preferred) or `product` (catalog fallback) */
  bottle?: Bottle;
  product?: VinmonopoletProduct;
  rating?: number;
  ratingCount?: number;
  tasteNote?: string;
  tastedWith?: string;
  draggableHandle?: React.ReactNode;
  highlight?: boolean;
  /** Selection mode: when set, the card becomes a checkbox toggle instead of a link */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /**
   * When set (and the card isn't archived/selectable), shows a small price-tag
   * button that opens the pricing slide-in for this bottle without navigating
   * away — used on the cellar page. Other callers of BottleCard omit this and
   * keep the plain Link-to-detail-page behaviour.
   */
  onOpenPricing?: (bottle: Bottle) => void;
}

export function BottleCard({
  bottle,
  product,
  rating,
  ratingCount,
  tasteNote,
  tastedWith,
  draggableHandle,
  highlight,
  selectable,
  selected,
  onToggleSelect,
  onOpenPricing,
}: BottleCardProps) {
  const src: Source | null = bottle
    ? { kind: 'bottle', bottle }
    : product
      ? { kind: 'catalog', product }
      : null;
  if (!src) return null;

  const view = src.kind === 'bottle' ? bottleToView(src.bottle) : productToView(src.product);
  const drinkUrgency = computeDrinkUrgency(view.drinkFromYear, view.drinkToYear);
  const archived = Boolean(view.status && view.status !== 'cellared');

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <Card
        interactive={!archived || selectable}
        glow={highlight || selected || (!archived && (drinkUrgency === 'overdue' || drinkUrgency === 'soon'))}
        className={`group relative grid gap-3 p-4 ${selected ? 'border-candle' : ''} ${archived ? 'opacity-75' : ''}`}
      >
        {draggableHandle && (
          <div className="absolute right-2 top-2 opacity-50 transition-opacity group-hover:opacity-100">
            {draggableHandle}
          </div>
        )}
        {onOpenPricing && bottle && !selectable && !archived && (
          <button
            type="button"
            aria-label="Prising"
            title="Prising"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenPricing(bottle);
            }}
            className="absolute right-2 top-2 z-10 grid h-6 w-6 place-items-center rounded-md border border-line-soft bg-ink/70 text-mist opacity-0 backdrop-blur-sm transition-opacity hover:border-candle hover:text-candle group-hover:opacity-100"
          >
            <Icon name="receipt" size={13} />
          </button>
        )}
        {selectable && (
          <button
            type="button"
            aria-label={selected ? 'Avmerk' : 'Velg'}
            aria-pressed={selected ?? false}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleSelect?.();
            }}
            className={`absolute left-2 top-2 z-10 grid h-6 w-6 place-items-center rounded-md border ${
              selected
                ? 'border-candle bg-candle text-on-primary'
                : 'border-line-soft bg-ink/70 text-mist backdrop-blur-sm hover:border-candle hover:text-candle'
            }`}
          >
            {selected ? <Icon name="check" size={14} /> : null}
          </button>
        )}
        {selectable ? (
          <button
            type="button"
            onClick={() => onToggleSelect?.()}
            className="grid gap-3 text-left w-full"
          >
            <CardInner
              view={view}
              rating={rating}
              ratingCount={ratingCount}
              tasteNote={tasteNote}
              tastedWith={tastedWith}
              drinkUrgency={drinkUrgency}
            />
          </button>
        ) : archived ? (
          <div className="grid gap-3 cursor-default">
            <CardInner
              view={view}
              rating={rating}
              ratingCount={ratingCount}
              tasteNote={tasteNote}
              tastedWith={tastedWith}
              drinkUrgency={drinkUrgency}
            />
          </div>
        ) : (
          <Link to={view.href} className="grid gap-3">
            <CardInner
              view={view}
              rating={rating}
              ratingCount={ratingCount}
              tasteNote={tasteNote}
              tastedWith={tastedWith}
              drinkUrgency={drinkUrgency}
            />
          </Link>
        )}
      </Card>
    </motion.div>
  );
}

export function BottleCardSkeleton() {
  return (
    <Card className="grid gap-3 p-4">
      <Skeleton height={152} rounded="md" />
      <Skeleton height={18} width="80%" />
      <Skeleton height={12} width="60%" />
      <div className="mt-2 flex items-center justify-between">
        <Skeleton height={20} width={70} rounded="pill" />
        <Skeleton height={12} width={50} />
      </div>
    </Card>
  );
}

function CardInner({
  view,
  rating,
  ratingCount,
  tasteNote,
  tastedWith,
  drinkUrgency,
}: {
  view: CardView;
  rating?: number;
  ratingCount?: number;
  tasteNote?: string;
  tastedWith?: string;
  drinkUrgency: DrinkUrgency;
}) {
  const archived = Boolean(view.status && view.status !== 'cellared');
  return (
    <>
      <div className="relative grid h-36 place-items-center overflow-hidden rounded-md bg-ink-soft sm:h-40 lg:h-36 xl:h-40 2xl:h-44">
        <BottleImage src={view.imageUrl} alt={view.name} iconSize={48} mode="lightbox" />
        {view.vintage && (
          <span className="absolute bottom-2 left-2 ui-badge ui-badge--gold">{view.vintage}</span>
        )}
        {view.qty != null && view.qty > 1 && (
          <span className="absolute bottom-2 right-2 ui-badge ui-badge--ruby">×{view.qty}</span>
        )}
        {rating != null && (
          <span className="absolute right-2 top-2 ui-badge ui-badge--ruby flex items-center gap-1">
            <Icon name="star" size={10} /> {formatRating(rating)}
          </span>
        )}
        {view.status && view.status !== 'cellared' && (
          <span className="absolute left-2 top-2 ui-badge ui-badge--mist">{statusLabel(view.status)}</span>
        )}
        {drinkUrgency === 'overdue' && view.status === 'cellared' && (
          <span className="absolute left-2 top-2 ui-badge ui-badge--danger flex items-center gap-1">
            <Icon name="warning" size={10} /> over drikkevindu
          </span>
        )}
        {drinkUrgency === 'soon' && view.status === 'cellared' && (
          <span className="absolute left-2 top-2 ui-badge ui-badge--gold flex items-center gap-1">
            <Icon name="clock" size={10} /> drikkes snart
          </span>
        )}
      </div>
      <div className="grid gap-1">
        <h3 className="font-serif text-base leading-tight text-parchment line-clamp-2">{view.name}</h3>
        <div className="flex items-center gap-2 text-xs text-mist">
          {view.region && <span className="truncate">{view.region}</span>}
          {view.abv != null && (
            <>
              <span aria-hidden>·</span>
              <span>{formatAbv(view.abv)}</span>
            </>
          )}
          {view.volume != null && (
            <>
              <span aria-hidden>·</span>
              <span>{formatVolumeMl(view.volume)}</span>
            </>
          )}
        </div>
        <div className="mt-2 grid gap-2">
          {view.pairings.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {view.pairings.slice(0, 3).map((pairing) => (
                <span key={pairing} className="ui-badge ui-badge--gold normal-case tracking-normal">
                  {pairing}
                </span>
              ))}
            </div>
          )}
          {view.labelLine && (
            <span className="text-[11px] uppercase tracking-wider text-mist line-clamp-1">
              {view.labelLine}
            </span>
          )}
          {(tasteNote || tastedWith || rating != null || archived) && (
            <div className="rounded-md border border-line bg-overlay-soft px-2 py-1.5 text-[11px] leading-snug text-parchment-dim">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {rating != null && (
                  <span className="inline-flex items-center gap-1 text-candle">
                    <Icon name="star" size={10} />
                    {formatRating(rating)}
                    {ratingCount && ratingCount > 1 ? ` (${ratingCount})` : ''}
                  </span>
                )}
                {tastedWith && <span>Med {tastedWith}</span>}
                {archived && <span>{statusLabel(view.status!)}</span>}
              </div>
              {tasteNote && <div className="mt-1 line-clamp-2">{tasteNote}</div>}
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {view.tags?.slice(0, 2).map((t) => (
              <Badge key={t} tone="mist">{t}</Badge>
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-mist">{view.id}</span>
        </div>
      </div>
    </>
  );
}

interface CardView {
  id: string;
  name: string;
  href: string;
  imageUrl?: string;
  vintage?: string;
  region?: string;
  abv?: number;
  volume?: number;
  qty?: number;
  status?: string;
  tags?: string[];
  drinkFromYear?: number;
  drinkToYear?: number;
  pairings: string[];
  labelLine?: string;
}

type DrinkUrgency = 'too-early' | 'in-window' | 'soon' | 'overdue' | 'unknown';

function computeDrinkUrgency(from?: number, to?: number): DrinkUrgency {
  const year = new Date().getFullYear();
  if (to == null && from == null) return 'unknown';
  if (to != null && year > to) return 'overdue';
  if (to != null && year >= to - 1) return 'soon';
  if (from != null && year < from) return 'too-early';
  return 'in-window';
}

function bottleToView(b: Bottle): CardView {
  const profile = bottleProfile(b);
  return {
    id: `#${b.id.slice(0, 6)}`,
    name: b.name,
    href: `/bottle/${b.id}`,
    imageUrl: b.photoUrl,
    vintage: b.vintage,
    region: b.region ?? b.country,
    qty: b.qty,
    status: b.status,
    tags: b.tags,
    drinkFromYear: b.drinkFromYear,
    drinkToYear: b.drinkToYear,
    pairings: profile.pairings,
    labelLine: profile.labelLine,
  };
}

function productToView(p: VinmonopoletProduct): CardView {
  const country = (p.origins as { country?: string } | undefined)?.country;
  const region = (p.origins as { region?: string } | undefined)?.region;
  return {
    id: `#${p.basic.productId}`,
    name: p.basic.productLongName ?? p.basic.productShortName ?? '—',
    href: `/bottle/${p.basic.productId}`,
    imageUrl: p.imageUrl,
    vintage: p.basic.vintage,
    region: region ?? country,
    abv: p.basic.alcoholContent,
    volume: p.basic.volume,
    pairings: productPairings(p),
    labelLine: [
      (p.classification as { mainCategory?: { name?: string } } | undefined)?.mainCategory?.name,
      formatAbv(p.basic.alcoholContent),
      formatVolumeMl(p.basic.volume),
    ].filter(Boolean).join(' · '),
  };
}

function bottleProfile(b: Bottle): { pairings: string[]; labelLine?: string } {
  const metadata = (b.metadata ?? {}) as Record<string, unknown>;
  const explicitPairings = asStringArray(
    metadata.foodPairings ?? metadata.food_pairings ?? metadata.pairings ?? metadata.food,
  );
  const pairings = explicitPairings.length > 0 ? explicitPairings : inferBottlePairings(b);
  const wineClass = firstString(metadata.wineClass, metadata.group, metadata.class);
  const wineType = firstString(metadata.wineType, metadata.type, categoryLabel(b.category));
  const valueRange = firstString(metadata.valueRange, metadata.priceRange, metadata.value);
  const labelLine = [
    wineClass ? classLabel(wineClass) : undefined,
    wineType,
    valueRange ? valueLabel(valueRange) : undefined,
  ].filter(Boolean).join(' · ');

  return { pairings, labelLine };
}

function productPairings(p: VinmonopoletProduct): string[] {
  const category = (p.classification as { mainCategory?: { name?: string } } | undefined)?.mainCategory?.name ?? '';
  const haystack = [
    p.basic.productLongName,
    p.basic.productShortName,
    category,
    (p.origins as { region?: string; country?: string } | undefined)?.region,
  ].join(' ').toLowerCase();
  if (/champagne|musserende|cava|cremant/.test(haystack)) return ['Fisk', 'Kylling', 'Aperitiff'];
  if (/hvit|riesling|chablis|sauvignon|chardonnay/.test(haystack)) return ['Fisk', 'Kylling', 'Ost'];
  if (/rød|rod|pinot|nebbiolo|barolo|bordeaux|rioja|sangiovese|cabernet|syrah/.test(haystack)) return ['Kjøtt', 'Kylling', 'Ost'];
  if (/øl|beer|stout|ipa|pils/.test(haystack)) return ['Grill', 'Snacks'];
  if (/brennevin|whisky|gin|rom|vodka|mezcal/.test(haystack)) return ['Cocktails', 'Avec'];
  return [];
}

function inferBottlePairings(b: Bottle): string[] {
  const haystack = [
    b.name,
    b.producer,
    b.region,
    b.country,
    b.category,
    ...b.grapeVarieties,
    ...b.tags,
    b.notes,
  ].filter(Boolean).join(' ').toLowerCase();

  const explicitFromTags = [
    ['fisk', 'Fisk'],
    ['sjømat', 'Fisk'],
    ['sjomat', 'Fisk'],
    ['kylling', 'Kylling'],
    ['kjøtt', 'Kjøtt'],
    ['kjott', 'Kjøtt'],
    ['ost', 'Ost'],
    ['aperitiff', 'Aperitiff'],
    ['dessert', 'Dessert'],
  ] as const;
  const fromTags = explicitFromTags
    .filter(([needle]) => haystack.includes(needle))
    .map(([, label]) => label);
  if (fromTags.length > 0) return unique(fromTags).slice(0, 3);

  if (b.category === 'beer') return ['Grill', 'Snacks'];
  if (b.category === 'spirits') return ['Cocktails', 'Avec'];
  if (/champagne|musserende|cava|cremant/.test(haystack)) return ['Fisk', 'Kylling', 'Aperitiff'];
  if (/riesling|chablis|sauvignon|chardonnay|hvit/.test(haystack)) return ['Fisk', 'Kylling', 'Ost'];
  if (/pinot|nebbiolo|barolo|bordeaux|rioja|sangiovese|cabernet|syrah|tempranillo|rød|rod/.test(haystack)) {
    return ['Kjøtt', 'Kylling', 'Ost'];
  }
  return [];
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return unique(value.map((item) => String(item).trim()).filter(Boolean));
  if (typeof value === 'string') return unique(value.split(/[|,]/).map((item) => item.trim()).filter(Boolean));
  return [];
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function classLabel(value: string): string {
  const match = value.match(/^G?([1-5])$/i);
  return match ? `Klasse ${match[1]}` : value;
}

function valueLabel(value: string): string {
  return /\bNOK\b/i.test(value) ? value : `${value} NOK`;
}

function categoryLabel(category?: string): string | undefined {
  switch (category) {
    case 'wine': return 'Vin';
    case 'beer': return 'Øl';
    case 'spirits': return 'Sprit';
    case 'cider': return 'Sider';
    case 'sake': return 'Sake';
    case 'mead': return 'Mjød';
    case 'other': return 'Annet';
    default: return undefined;
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'drunk': return 'Drukket';
    case 'gifted': return 'Bortgitt';
    case 'cellared': return 'I kjelleren';
    default: return status;
  }
}

function formatRating(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
}
